import type { GraphQLError } from 'graphql';
import type {
  Bucket,
  HomeLocationDescriptionInput,
  SearchFilters,
  RightToWorkDisplayLabelInput,
  NationalityLabelInput,
} from '../stores/JobApplicationStore/types';
import { logError } from '../utils/logError';
import { convertToUnderScoreUpperCase } from '../utils/convertToUnderScoreUpperCase';
import type { Sort, SortOrder } from './types';
import { DEFAULT_SORT_OPTION, SORT_OPTIONS } from '../constants/sortOrder';
import type { LoadJobApplicationsParams } from '../stores/JobApplicationStore';
import { getAdvertiserIds } from 'src/utils/advertiserIds';
import type { NationalityInput } from 'src/App/AppPageBlock/graphql/cm-types.generated';

const removeNullValueFromObject = (object: Record<string, any>): void => {
  Object.keys(object).forEach(
    (key) => object[key] === null && delete object[key],
  );
};

const removeNullValueFromArray = (objects: [Record<string, any>]): void => {
  objects.forEach((object: Record<string, any>) => {
    removeNullValueFromObject(object);
  });
};

const findAuthenticationError = (
  errors: readonly GraphQLError[] | undefined,
): GraphQLError | undefined => {
  if (errors) {
    return errors.find(
      (error) =>
        error.extensions && error.extensions.code === 'UNAUTHENTICATED',
    );
  }
  return;
};

const findForbiddenError = (
  errors: readonly GraphQLError[] | undefined,
): GraphQLError | undefined => {
  if (errors) {
    return errors.find(
      (error) => error.extensions && error.extensions.code === 'FORBIDDEN',
    );
  }
  return;
};

interface FilterOptions {
  advertiserId: string;
  searchText?: string;
  sort: Sort;
  sortOrder: SortOrder;
  bucket?: Bucket;
  pageNumber?: number;
  filters: SearchFilters;
  pageSize?: number;
  homeLocationDescriptionInput: HomeLocationDescriptionInput;
  rightToWorkDisplayLabelInput: RightToWorkDisplayLabelInput;
  nationalityInput: NationalityInput;
  nationalityLabelInput: NationalityLabelInput;
}
interface JobId {
  jobId: number;
}
interface PositionId {
  positionId: number;
}

interface DisplayCountry {
  displayCountry: string;
}

export type ProspectsWithPaginationByJobIdProps = FilterOptions & JobId;
export type ProspectsWithPaginationByPositionIdProps = FilterOptions &
  PositionId;

const getSort = (sortBy: string | undefined, bucket: string | undefined) => {
  let sort;
  sort = SORT_OPTIONS.find((option) => option.value === sortBy);
  if (!sort) {
    logError(
      {
        errorCode: 'UNKNOWN_SORT_TYPE',
        description: 'Sort option unable to be found - Unknown sort option',
      },
      {
        requestedSortOption: sortBy,
        bucket,
      },
    );
    sort = DEFAULT_SORT_OPTION;
  }
  const sortApiKey = sort.apiKey;
  const sortOrder = sort.sortOrder;

  const enumSort = convertToUnderScoreUpperCase(sortApiKey) as Sort;
  const enumSortOrder = convertToUnderScoreUpperCase(sortOrder) as SortOrder;

  return {
    sort: enumSort,
    sortOrder: enumSortOrder,
  };
};

const getBucket = (
  bucket: Bucket | undefined,
  searchText: string | undefined,
) => (searchText === '' ? bucket : undefined);

export const buildProspectsWithPaginationByJobIdInput = (
  options: LoadJobApplicationsParams & JobId & DisplayCountry,
) => {
  const {
    sortBy,
    bucket,
    searchText,
    page,
    filters,
    jobId,
    language,
    displayCountry,
  } = options;

  const variables: ProspectsWithPaginationByJobIdProps = {
    ...getSort(sortBy, bucket),
    jobId,
    advertiserId: getAdvertiserIds().seekAdvertiserId as string,
    searchText,
    bucket: getBucket(bucket, searchText),
    filters,
    pageNumber: page,
    homeLocationDescriptionInput: { language, displayCountry },
    rightToWorkDisplayLabelInput: { language },
    nationalityInput: { jobId },
    nationalityLabelInput: { language },
  };

  return variables;
};

export {
  removeNullValueFromObject,
  removeNullValueFromArray,
  findAuthenticationError,
  findForbiddenError,
};
